import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { useRef } from "react";
import { useAnalyticsTracker } from "~/app/hooks/analytics/useAnalyticsTracker";
import { usePWAInstall } from "~/app/hooks/usePWAInstall";
import iconMore from "~/assets/btn_more.svg";
import iconShare from "~/assets/btn_share.svg";
import Diamond from "~/assets/diamonds/small.png";
import WofLogo from "~/assets/icon_pwa.png";
import WofLogoSmall from "~/assets/icon_pwa_small.png";
import IOSAdd from "~/assets/pwa_action_ios.png";
import ShinePurple from "~/assets/shine_purple.png";
import { CTAButton } from "../CTAButton/CTAButton";

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiPaper-root": {
    borderRadius: "24px",
    // border: "3px solid #CEB0FA",
    // background: "linear-gradient(180deg, #FFF 0%, #CEB0FA 100%)",
    backgroundColor: "transparent",
    overflow: "visible",
    margin: "8px",
  },
}));

type TDialogSafariInstall = {
  open: boolean;
  onClose: (value: boolean) => void;
};

export const DialogSafariInstall = ({ open, onClose }: TDialogSafariInstall) => {
  const { isInstallable } = usePWAInstall();
  const trackUI = useAnalyticsTracker("sys.ui");
  const hasTrackedPageLoadRef = useRef(false);

  const onGotItClick = () => {
    onClose(false);
    if (!hasTrackedPageLoadRef.current) {
      trackUI({
        screen_id: "install_pwa",
        screen_name: "web_portal",
        screen_type: "pop-up",
        screen_category: "web_portal_pwa",
        screen_subcategory: "web_portal_pwa",
        screen_content: isInstallable ? "prompt" : "manual_ios",
        screen_section: null,
        previous_screen_id: null,
        previous_screen_name: null,
        previous_screen_category: null,
        interaction_type: "tap",
        interaction_object: "install_pwa_button",
        interaction_object_category: "display",
        flow_name: "web_portal",
        flow_order: 2,
      });
      hasTrackedPageLoadRef.current = true;
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} aria-labelledby="install-mobile">
      <img src={WofLogo} alt="Logo" width="125px" style={{ margin: "0 auto -98px", zIndex: 9 }} />
      <DialogContent
        sx={{
          px: { xs: 1, sm: 3 },
          maxWidth: "440px",
          borderRadius: "24px",
          border: "3px solid #CEB0FA",
          background: "linear-gradient(180deg, #FFF 0%, #CEB0FA 100%)",
          paddingTop: "70px",
          position: "relative",
        }}
      >
        <Box
          sx={{
            backgroundImage: `url(${ShinePurple})`,
            backgroundRepeat: "no-repeat",
            backgroundPositionY: "-374px",
            backgroundPositionX: "center",
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="12px"
          fontFamily="Lato"
          color="#382053"
          sx={{
            position: "relative",
            zIndex: 5,
          }}
        >
          <Typography fontSize="20px" fontWeight="800" textAlign="center">
            Our best offers at your fingertips!
          </Typography>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="start"
            alignItems="start"
            gap={1}
            width="90%"
          >
            <Typography fontSize="20px" fontWeight="600">
              1.
            </Typography>
            <Typography
              fontSize="20px"
              sx={{
                display: "block",
                alignItems: "start",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <span>
                Tap the <b>More</b>{" "}
              </span>
              <img src={iconMore} alt="more icon" width="24px" height="24px" />{" "}
              <span>
                at the bottom <b>or</b>{" "}
              </span>
              <span>
                Tap the <img src={iconShare} alt="icon share" width="24px" height="24px" />{" "}
                <b>button</b>
              </span>
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="start"
            alignItems="start"
            gap={1}
            width="90%"
          >
            <Typography fontSize="20px" fontWeight="600">
              2.
            </Typography>
            <Typography fontSize="20px" fontWeight="600">
              Scroll down and select
              <img src={IOSAdd} alt="Add to Home Screen" />
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="start"
            alignItems="start"
            gap={1}
            width="90%"
          >
            <Typography fontSize="20px" fontWeight="600">
              3.
            </Typography>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="start"
              alignItems="center"
              gap="4px"
            >
              <Typography flex={1} fontSize="20px">
                Tap the <b>WoF Web Store</b> icon on your home screen
              </Typography>
              <img src={WofLogoSmall} alt="logo" width="42px" height="42px" />
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="start"
            alignItems="start"
            gap={1}
            width="90%"
          >
            <Typography fontSize="20px" fontWeight="600">
              4.
            </Typography>
            <Typography
              fontSize="20px"
              sx={{
                display: "block",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <span>Claim your free reward</span>{" "}
              <img
                src={Diamond}
                alt="reward"
                width="32px"
                height="32px"
                style={{ marginBottom: "-8px" }}
              />{" "}
              <span>
                <b>200</b>
              </span>
            </Typography>
          </Box>

          <CTAButton onClick={onGotItClick} sx={{ width: "190px" }}>
            <Typography
              variant="caption"
              fontFamily="Lato"
              fontWeight="600"
              sx={{
                fontSize: "min(6vmin, 28px)",
                lineHeight: "min(calc(7vmin), 40px)",
                textShadow: "0 2px 0 #4A4A4A",
              }}
            >
              Got it
            </Typography>
          </CTAButton>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};
